import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getQuoteChecklistDetails, updateQuoteStageAndTasks } from "../../../Api";
import { showSwal } from "../../../Helper/ShowAlert";
import { TRANSPORTERS } from "../../../Helper/partnerLabels";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faListCheck, faCheckCircle, faTimes, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";

const STAGES = {
  QUERY_STAGE: "query_stage",
  QUOTE_STAGE: "quote_stage",
  NOMINATION_STAGE: "nomination_stage",
  COLLECTION_STAGE: "collection_stage",
  AIRLINE_BOOKING_STAGE: "airline_booking_stage",
  DOCUMENTATION_STAGE: "documentation_stage",
  HANDOVER_STAGE: "handover_stage",
  POST_DELIVERY_STAGE: "post_delivery_stage",
  INVOICING_BILLING_STAGE: "invoicing_billing_stage",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

const CHECKLIST_TASKS = {
  [STAGES.QUERY_STAGE]: [
    { id: "QUERY_STAGE_ACKNOWLEDGE_QUERY", label: "Acknowledge Customer Query" },
    { id: "QUERY_STAGE_GATHER_MISSING_INFO", label: "Gather Missing Information" },
    // { id: "QUERY_STAGE_CONTACT_VENDORS", label: "Contact Vendors for Initial Rates" },
    // { id: "QUERY_STAGE_CALCULATE_COSTS", label: "Calculate Initial Costs" },
    { id: "QUERY_STAGE_SHIPPER_DETAIL_REQUEST", label: "Gather Shipper Details From Customer" },
    { id: "QUERY_STAGE_COORDINATE_CUSTOMER_DETAILS", label: "Co-ordinate with customer for their details" },
  ],
  [STAGES.QUOTE_STAGE]: [
    { id: "QUOTE_STAGE_SEND_AIRLINE_RATE_REQUESTS", label: "Send rate requests to Airlines" },
    { id: "QUOTE_STAGE_SEND_HAULER_RATE_REQUESTS", label: `Send rate requests to ${TRANSPORTERS}` },
    { id: "QUOTE_STAGE_PREPARE_FINAL_QUOTE_COMPARISON", label: "Prepare final quote cost comparison table" },
    { id: "QUOTE_STAGE_SEND_FINAL_QUOTE", label: "Send final quote to customer" },
  ],
  [STAGES.NOMINATION_STAGE]: [
    { id: "NOMINATION_STAGE_NOMINATE_AN_OPTION", label: "Nominate an option received from the customer" },
    // { id: "NOMINATION_STAGE_GET_CONFIRMATION", label: "Confirmation for the selected option" },
    { id: "NOMINATION_STAGE_GATHER_SHIPPER_DOCS", label: "Gather Shipper Documents" },
    { id: "NOMINATION_STAGE_VERIFY_EORI_LICENSES", label: "Verify EORI/Export Licenses" },
    { id: "NOMINATION_STAGE_CONFIRM_COLLECTION_DETAILS", label: "Confirm Collection Details with Shipper" },
  ],
  [STAGES.COLLECTION_STAGE]: [
    { id: "COLLECTION_STAGE_GENERATE_COLLECTION_NOTE", label: "Generate Collection Note" },
    { id: "COLLECTION_STAGE_DISPATCH_TRANSPORTER", label: "Dispatch Transporter for Collection" },
    { id: "COLLECTION_STAGE_CONFIRM_COLLECTION", label: "Confirm Item Collection" },
    { id: "COLLECTION_STAGE_RECEIVE_GRN", label: "Receive Goods Receipt Note (GRN)" },
  ],
  [STAGES.AIRLINE_BOOKING_STAGE]: [
    { id: "AIRLINE_BOOKING_STAGE_CHECK_FLIGHT_SCHEDULES", label: "Check Flight Schedules" },
    { id: "AIRLINE_BOOKING_STAGE_BOOK_FLIGHT", label: "Book Airline Flight" },
    { id: "AIRLINE_BOOKING_STAGE_RECEIVE_BOOKING_CONFIRMATION", label: "Receive Booking Confirmation" },
  ],
  [STAGES.DOCUMENTATION_STAGE]: [
    { id: "DOCUMENTATION_STAGE_DRAFT_AWB_MANIFEST", label: "Draft AWB & Manifest" },
    { id: "DOCUMENTATION_STAGE_SEND_AWB_DRAFT_FOR_APPROVAL", label: "Send AWB Draft for Approval" },
    { id: "DOCUMENTATION_STAGE_GET_AWB_APPROVED", label: "Get AWB Approved" },
    { id: "DOCUMENTATION_STAGE_FINAL_AWB_PRINT", label: "Print Final AWB" },
    { id: "DOCUMENTATION_STAGE_PREPARE_CUSTOMS_DOCS", label: "Prepare Customs Documents" },
  ],
  [STAGES.HANDOVER_STAGE]: [
    { id: "HANDOVER_STAGE_DELIVER_TO_GHA", label: "Deliver Goods to GHA" },
    { id: "HANDOVER_STAGE_CONFIRM_GHA_RECEIPT", label: "Confirm GHA Receipt" },
    { id: "HANDOVER_STAGE_INITIATE_CUSTOMS_CLEARANCE", label: "Initiate Customs Clearance" },
    { id: "HANDOVER_STAGE_MONITOR_CUSTOMS_STATUS", label: "Monitor Customs Status" },
  ],
  [STAGES.POST_DELIVERY_STAGE]: [
    { id: "POST_DELIVERY_STAGE_TRACK_FLIGHT", label: "Track Flight" },
    { id: "POST_DELIVERY_STAGE_CONFIRM_DEPARTURE", label: "Confirm Departure" },
    { id: "POST_DELIVERY_STAGE_MONITOR_TRANSIT", label: "Monitor Transit" },
    { id: "POST_DELIVERY_STAGE_RESOLVE_SNAGS", label: "Resolve Any Snags" },
    { id: "POST_DELIVERY_STAGE_SEND_FINAL_PRE_ALERT", label: "Send Final Pre-Alert" },
  ],
  [STAGES.INVOICING_BILLING_STAGE]: [
    { id: "INVOICING_BILLING_STAGE_GENERATE_INVOICE", label: "Generate Invoice" },
    { id: "INVOICING_BILLING_STAGE_SEND_INVOICE", label: "Send Invoice to Customer" },
    { id: "INVOICING_BILLING_STAGE_MONITOR_PAYMENT", label: "Monitor Payment Status" },
    { id: "INVOICING_BILLING_STAGE_CONFIRM_PAYMENT", label: "Confirm Payment Received" },
  ],
  [STAGES.COMPLETED]: [
    { id: "COMPLETED_SHIPMENT_FINALIZED", label: "Shipment Finalized" },
  ],
  [STAGES.CANCELLED]: [
    { id: "CANCELLED_SHIPMENT_TERMINATED", label: "Shipment Terminated" },
  ],
};

function StageChecklistDropdown({ quoteId, setCurrentStage, position, onClose, inline = false }) {
  const loginToken = useSelector((state) => state.reducer.userToken);
  const userLoggedIn = useSelector((state) => state.reducer.userLoggedIn);
  const [quoteData, setQuoteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const initialCompletedTasksRef = useRef([]);

  const isInline = inline || !position;

  const fetchChecklist = async () => {
    if (!quoteId || !loginToken) {
      setLoading(false);
      setError("Quote ID or login token is missing.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await getQuoteChecklistDetails({ token: loginToken, quoteId, role: userLoggedIn?.role });
      if (response.status === 200 && response.checklist) {
        setQuoteData(response.checklist);
        setCurrentStage(response.checklist?.currentStage);
        initialCompletedTasksRef.current = [...response.checklist.completedTasks];
        setHasChanges(false);
      } else {
        setError(response.message || "Failed to fetch checklist details.");
        setQuoteData(null);
      }
    } catch (err) {
      console.error("Error fetching checklist details:", err);
      setError("An error occurred while fetching checklist details.");
      setQuoteData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChecklist();
  }, [quoteId, loginToken]);

  const currentStageChecklist = quoteData ? CHECKLIST_TASKS[quoteData.currentStage] || [] : [];

  const formatStageName = (stage) => {
    return stage.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const allTasksCompleted = currentStageChecklist.length > 0 &&
    currentStageChecklist.every(task => quoteData?.completedTasks.includes(task.id));

  const handleCheckboxChange = (taskId) => {
    setQuoteData(prevQuoteData => {
      const currentCompletedTasks = prevQuoteData.completedTasks || [];
      const isCurrentlyChecked = currentCompletedTasks.includes(taskId);

      const newCompletedTasks = isCurrentlyChecked
        ? currentCompletedTasks.filter(id => id !== taskId)
        : [...currentCompletedTasks, taskId];

      const initialTasksSet = new Set(initialCompletedTasksRef.current);
      const newTasksSet = new Set(newCompletedTasks);

      const changed = newCompletedTasks.length !== initialCompletedTasksRef.current.length ||
        ![...newTasksSet].every(item => initialTasksSet.has(item));

      setHasChanges(changed);

      return {
        ...prevQuoteData,
        completedTasks: newCompletedTasks,
      };
    });
  };

  const handleUpdateChecklist = async () => {
    if (!quoteData || !loginToken || isUpdating || !hasChanges) return;

    setIsUpdating(true);
    try {
      const response = await updateQuoteStageAndTasks({
        token: loginToken,
        quoteId: quoteId,
        role: userLoggedIn?.role,
        data: {
          taskIds: quoteData.completedTasks,
        },
      });

      if (response.status === 200) {
        showSwal("Success", "Checklist updated successfully!", 200);
        fetchChecklist();
      } else {
        showSwal("Error", response.message || "Failed to update checklist.", response.status || 500);
      }
    } catch (err) {
      console.error("Error updating checklist:", err);
      showSwal("Error", "An error occurred while updating checklist.", 500);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleNextStage = async () => {
    if (!quoteData || !loginToken || isUpdating || !allTasksCompleted) return;

    const allStages = Object.values(STAGES);
    const currentIndex = allStages.indexOf(quoteData.currentStage);
    const nextStage = allStages[currentIndex + 1];

    if (!nextStage) {
      showSwal("Info", "This is the last stage or no next stage defined!", "info");
      return;
    }

    setIsUpdating(true);
    try {
      const response = await updateQuoteStageAndTasks({
        token: loginToken,
        quoteId: quoteId,
        role: userLoggedIn?.role,
        data: {
          newStage: nextStage,
        },
      });

      if (response.status === 200) {
        showSwal("Success", `Moved to ${formatStageName(nextStage)}!`, 200);
        fetchChecklist();
      } else {
        showSwal("Error", response.message || "Failed to move to next stage.", response.status || 500);
      }
    } catch (err) {
      console.error("Error moving to next stage:", err);
      showSwal("Error", "An error occurred while moving to the next stage.", 500);
    } finally {
      setIsUpdating(false);
    }
  };

  const completedCount = quoteData?.completedTasks?.length || 0;
  const totalCount = currentStageChecklist.length;
  const progressPercentage = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const containerClass = isInline
    ? "w-full rounded-[18px]"
    : "stage-checklist-portal rounded-xl w-80 max-w-md bg-white/95 dark:bg-[oklch(0.26_0.028_264/0.95)] backdrop-blur-[20px] shadow-[0_8px_32px_rgba(0,0,0,0.12)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-white/25 dark:border-[oklch(0.33_0.022_264/0.5)]";

  const containerStyle = isInline
    ? {}
    : {
        position: 'fixed',
        top: `${position.top}px`,
        right: `${position.right}px`,
        WebkitBackdropFilter: 'blur(20px)',
        fontFamily: 'inherit',
        zIndex: 99999,
        maxHeight: '80vh',
        overflowY: 'auto',
      };

  return (
    <motion.div
      className={containerClass}
      style={containerStyle}
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.2 }}
    >
      <div className={isInline ? "" : "p-4"}>
        {!isInline && (
          <div className="flex items-center justify-between mb-4">
            <h3
              className="font-semibold flex items-center gap-2 text-gray-900 dark:text-gray-100"
              style={{
                fontFamily: 'inherit',
                letterSpacing: '0.2px',
                fontWeight: 600
              }}
            >
              <FontAwesomeIcon icon={faListCheck} style={{ fontSize: '14px', color: '#10B981' }} />
              Stage Checklist
            </h3>
            <motion.button
              onClick={onClose}
              className="p-1 rounded-lg transition-all duration-200 text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-[oklch(0.3_0.024_264/0.5)]"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '14px' }} />
            </motion.button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <FontAwesomeIcon icon={faSpinner} spin className="text-gray-700 dark:text-gray-300" style={{ fontSize: '20px' }} />
          </div>
        ) : error ? (
          <div className="text-sm text-red-500 py-4">{error}</div>
        ) : !quoteData ? (
          <div className="text-sm text-gray-700 dark:text-gray-300">No checklist data available.</div>
        ) : (
          <>
            <div className="mb-4">
              <div className="mb-2 flex items-center justify-between">
                <span
                  className="text-[14px] font-semibold text-slate-800 dark:text-slate-200"
                >
                  {formatStageName(quoteData.currentStage)}
                </span>
                <span
                  className="text-[12px] text-slate-500 dark:text-slate-400"
                >
                  {completedCount}/{totalCount}
                </span>
              </div>
              <div
                className="h-2 overflow-hidden rounded-full bg-slate-200 dark:bg-white/10"
              >
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: 'linear-gradient(90deg, rgba(16, 185, 129, 0.8), rgba(5, 150, 105, 0.9))'
                  }}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            <div className="mb-4 space-y-2">
              {currentStageChecklist.length > 0 ? (
                currentStageChecklist.map((item) => {
                  const isChecked = quoteData.completedTasks.includes(item.id);
                  return (
                    <motion.div
                      key={item.id}
                      className={`flex items-start space-x-3 rounded-2xl border px-3 py-3 transition-all duration-200 ${
                        isChecked
                          ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10'
                          : 'border-slate-200 bg-slate-50 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.225_0.026_264/0.6)]'
                      }`}
                      whileHover={{}}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleCheckboxChange(item.id)}
                        className="mt-0.5 h-4 w-4 cursor-pointer rounded"
                        style={{ accentColor: '#10B981' }}
                      />
                      <span
                        className={`flex-1 text-[13px] leading-5 ${isChecked ? 'text-slate-500 dark:text-slate-400 line-through' : 'text-slate-800 dark:text-slate-100'}`}
                        style={{
                          fontFamily: 'inherit',
                          letterSpacing: '0.2px'
                        }}
                      >
                        {item.label}
                      </span>
                      {isChecked && (
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          style={{ fontSize: '14px', color: '#10B981' }}
                        />
                      )}
                    </motion.div>
                  );
                })
              ) : (
                <div className="text-sm py-4 text-gray-600 dark:text-gray-400">
                  No checklist tasks defined for this stage.
                </div>
              )}
            </div>

            <div className="flex gap-2 border-t border-slate-200 pt-3 dark:border-white/10">
              <motion.button
                onClick={allTasksCompleted ? handleNextStage : handleUpdateChecklist}
                disabled={isUpdating || !hasChanges}
                className={`flex-1 rounded-xl px-3 py-2.5 text-[13px] font-semibold transition-all duration-200 ${(isUpdating || !hasChanges)
                    ? 'cursor-not-allowed bg-slate-300 opacity-60 dark:bg-[oklch(0.3_0.024_264)]'
                    : allTasksCompleted
                      ? 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-700'
                      : 'bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 dark:hover:bg-blue-700'
                  } text-white`}
                style={{
                  fontFamily: 'inherit',
                  letterSpacing: '0.2px'
                }}
                whileHover={(isUpdating || !hasChanges) ? {} : { scale: 1.02 }}
                whileTap={(isUpdating || !hasChanges) ? {} : { scale: 0.98 }}
              >
                {isUpdating ? "Updating..." : !allTasksCompleted ? "Update" : "Next Stage"}
              </motion.button>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

export default StageChecklistDropdown;

